import { darken } from 'polished';
import { css } from 'styled-components';

// Switzer is self-hosted via next/font/local in pages/_app.js, which defines --font-switzer.
const sans = 'var(--font-switzer), ui-sans-serif, system-ui, sans-serif';

export const fonts = {
  primary: sans,
  title: sans,
  other: sans,
};

export const human = {
  header: {
    fontFamily: fonts.title,
    fontSize: 32,
  },
  title: {
    fontFamily: fonts.primary,
    fontSize: 24,
  },
  subtitle: {
    fontFamily: fonts.primary,
    fontSize: 18,
  },
  body: {
    fontFamily: fonts.primary,
    fontSize: 14,
  },
};

/** Raw brand hex values. Safe for JS colour maths (polished, canvas, QR codes). */
export const brandColors = {
  primary: '#4B4DF1',
  secondary: '#4B4DF1',
  accent: '#E4517F',
  disabled: '#dedede',
};

export const maxContentWidth = '1200px';

export const breakpoints = {
  xs: 400,
  sm: 615,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 3000,
};

export const media = Object.keys(breakpoints).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (max-width: ${breakpoints[label]}px) {
      ${css(...args)};
    }
  `;
  return acc;
}, {});

export const minMedia = Object.keys(breakpoints).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (min-width: ${breakpoints[label]}px) {
      ${css(...args)};
    }
  `;
  return acc;
}, {});

/**
 * Mobile-first breakpoints matching the marketing site (Tailwind sm / md / lg / xl).
 * Use these in new code: `${up.md} { ... }`.
 */
export const screens = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
};

export const up = Object.keys(screens).reduce((acc, label) => {
  acc[label] = `@media (min-width: ${screens[label]}px)`;
  return acc;
}, {});

export const darkMode = '@media (prefers-color-scheme: dark)';

/**
 * A colour at `percent` opacity. Works with hex values AND CSS variables, so use
 * it instead of polished (`transparentize`, `rgba`) or hex-alpha suffixes
 * (`${color}CC`), neither of which can handle `var(--token)`.
 */
export const alpha = (color, percent) => `color-mix(in srgb, ${color} ${percent}%, transparent)`;

/**
 * Legacy hex palette. Still the palette of forced-light and self-themed pages
 * (unsupported-platform, dapp), whose components do colour maths on theme values.
 */
export const colors = {
  ...brandColors,
  background: {
    primary: '#F7F8FD',
    secondary: '#f3f3f3',
    accent: '#E9F1F7',
    warning: 'rgba(247, 197, 0, 0.15)',
    navbar: '#ffffff',
    card: '#FFFFFF',
  },
  typography: {
    primary: '#121212',
    secondary: darken(0.125, '#4C4C71'),
    subtle: '#B2B3C4',
    inverted: '#F1F3F5',
    disabled: '#efefef',
  },
  status: {
    danger: '#EB3449',
    info: '#32C5FF',
    success: '#2DD8A4',
    warning: '#F2C94C',
  },
};

/**
 * The same keys mapped to design tokens (src/themes/tokens.js), so existing
 * components pick up the new look and dark mode without being touched.
 * These are `var(--…)` strings: never pass them to polished or append hex alpha.
 */
export const tokenColors = {
  primary: 'var(--primary)',
  secondary: 'var(--primary)',
  accent: 'var(--brand-pink)',
  disabled: 'var(--border)',
  background: {
    primary: 'var(--background)',
    secondary: 'var(--muted)',
    accent: 'var(--accent)',
    warning: 'rgba(247, 197, 0, 0.15)',
    navbar: 'var(--background)',
    card: 'var(--card)',
  },
  typography: {
    primary: 'var(--foreground)',
    secondary: 'var(--muted-foreground)',
    subtle: 'var(--ink-subtle)',
    inverted: 'var(--primary-foreground)',
    disabled: 'var(--muted)',
  },
  status: colors.status,
};

const shadows = {
  primary: '0 4px 12px 0 rgba(74,144,226,0.08), 0 1px 2px 0 rgba(0,0,0,0.15)',
  // primary: '0 .125em .25em rgba(0, 0, 0, .125), 0 .5em 1em rgba(0, 0, 0, .05)',
  hover: '0 .25em .5em rgba(74,144,226, .085), 0 .75em 1.25em rgba(0, 0, 0, .05)',
  card: {
    default: 'rgba(0, 0, 0, 0.06) 0px 1px 3px 0px, rgba(0, 0, 0, 0.10) 0px 1px 2px 1px',
    hover: '0 .25em .5em rgba(0, 0, 0, .085), 0 .75em 1.25em rgba(0, 0, 0, .05)',
  },
};

/**
 * Hex theme for pages that are forced light (`Page.forcedTheme = 'light'`) or
 * manage their own theme class (`Page.selfThemed = true`, the dapp).
 */
export const legacyTheme = {
  name: 'light',
  fonts,
  maxContentWidth,
  colors,
  hex: brandColors,
  shadows,
};

/** Default storefront theme: every colour resolves to a design token. */
const theme = {
  name: 'light',
  fonts,
  maxContentWidth,
  colors: tokenColors,
  hex: brandColors,
  shadows,
};

/** Optionally override theme.colors.primary from a dapp provider's brandColor. */
export function resolveTheme(provider, base = theme) {
  const brandColor = provider?.brandColor;
  if (!brandColor) return base;
  return {
    ...base,
    colors: {
      ...base.colors,
      primary: brandColor,
    },
  };
}

export default theme;
